import * as React from "react";
import { Card, Button } from "@mira/ui";
import { AlertTriangle, RefreshCw } from "lucide-react";

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

interface ErrorBoundaryProps {
  children: React.ReactNode;
  fallback?: React.ReactNode;
}

export class DashboardErrorBoundary extends React.Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error("Dashboard error:", error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="min-h-[60vh] flex items-center justify-center px-4">
          <Card
            variant="white"
            padding="lg"
            className="max-w-md w-full text-center"
          >
            <div className="w-16 h-16 rounded-full bg-coral-alert/10 flex items-center justify-center mx-auto mb-4">
              <AlertTriangle className="w-8 h-8 text-coral-alert" />
            </div>
            <h2 className="text-subheading font-medium text-near-black dark:text-dark-text mb-2">
              Something went wrong
            </h2>
            <p className="text-body text-graphite dark:text-dark-muted mb-6">
              {this.state.error?.message || "An unexpected error occurred."}
            </p>
            <Button
              variant="primary"
              className="gap-2"
              onClick={() => window.location.reload()}
            >
              <RefreshCw className="w-4 h-4" />
              Reload page
            </Button>
          </Card>
        </div>
      );
    }

    return this.props.children;
  }
}
